import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { AlertTriangle, AlertCircle, Info, Clock, ChevronRight, ChevronLeft, CheckCircle2 } from 'lucide-react';

const priorityConfig = {
  critical: { 
    label: 'Critical', 
    icon: AlertTriangle, 
    bgColor: 'bg-red-50 dark:bg-red-950',
    borderColor: 'border-red-200 dark:border-red-800',
    textColor: 'text-red-700 dark:text-red-300',
    badgeColor: 'bg-red-500 text-white',
    checkboxColor: 'border-red-500 data-[state=checked]:bg-red-500'
  },
  warning: { 
    label: 'Warning', 
    icon: AlertCircle, 
    bgColor: 'bg-amber-50 dark:bg-amber-950',
    borderColor: 'border-amber-200 dark:border-amber-800',
    textColor: 'text-amber-700 dark:text-amber-300',
    badgeColor: 'bg-amber-500 text-white',
    checkboxColor: 'border-amber-500 data-[state=checked]:bg-amber-500'
  },
  info: { 
    label: 'Info', 
    icon: Info, 
    bgColor: 'bg-blue-50 dark:bg-blue-950',
    borderColor: 'border-blue-200 dark:border-blue-800',
    textColor: 'text-blue-700 dark:text-blue-300',
    badgeColor: 'bg-blue-500 text-white',
    checkboxColor: 'border-blue-500 data-[state=checked]:bg-blue-500'
  }
};

export const ForcedAnnouncementPopup = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [acknowledging, setAcknowledging] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const fetchForcedPopups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getForcedPopupAnnouncements();
      setAnnouncements(response.forced_popups || []);
      setCurrentIndex(0);
      setConfirmed(false);
    } catch (error) {
      console.error('Failed to fetch forced popups:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchForcedPopups();
  }, [fetchForcedPopups]);

  // Reset confirmation when switching announcements
  useEffect(() => {
    setConfirmed(false);
  }, [currentIndex]);

  const handleAcknowledge = async () => {
    if (!announcements[currentIndex] || !confirmed) return;
    
    try {
      setAcknowledging(true);
      await api.acknowledgeAnnouncement(announcements[currentIndex].id, 'app');
      
      // Remove acknowledged announcement
      const newAnnouncements = announcements.filter((_, i) => i !== currentIndex);
      setAnnouncements(newAnnouncements);
      setConfirmed(false);
      
      // Adjust index if needed
      if (currentIndex >= newAnnouncements.length && newAnnouncements.length > 0) {
        setCurrentIndex(newAnnouncements.length - 1);
      }
    } catch (error) {
      console.error('Failed to acknowledge announcement:', error);
    } finally {
      setAcknowledging(false);
    }
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(announcements.length - 1, prev + 1));
  };

  const formatTimeRemaining = (untilDate) => {
    if (!untilDate) return '';
    const until = new Date(untilDate);
    const now = new Date();
    const diffMs = until - now;
    
    if (diffMs <= 0) return 'Expiring soon';
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days > 1 ? 's' : ''} remaining`;
    }
    
    return `${hours}h ${minutes}m remaining`;
  };

  // Don't render anything if no announcements or still loading
  if (loading || announcements.length === 0) {
    return null;
  }

  const current = announcements[currentIndex];
  const config = priorityConfig[current.priority] || priorityConfig.info;
  const PriorityIcon = config.icon;

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent 
        className={`max-w-lg ${config.bgColor} ${config.borderColor} border-2`}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton
        data-testid="forced-announcement-popup"
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge className={config.badgeColor}>
                <PriorityIcon className="h-3 w-3 mr-1" />
                {config.label} Announcement
              </Badge>
              {announcements.length > 1 && (
                <span className="text-xs text-muted-foreground">
                  {currentIndex + 1} of {announcements.length}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              {formatTimeRemaining(current.force_popup_until)}
            </div>
          </div>
          <DialogTitle className={`text-lg ${config.textColor} mt-2`}>
            Important Notice
          </DialogTitle>
          <DialogDescription className="sr-only">
            This is a mandatory announcement that requires acknowledgment
          </DialogDescription>
        </DialogHeader>
        
        <div className={`py-4 px-3 rounded-lg bg-white/50 dark:bg-black/20 ${config.borderColor} border`}>
          <p className={`text-base leading-relaxed ${config.textColor}`}>
            {current.message}
          </p>
        </div>
        
        <div className="text-xs text-muted-foreground">
          <p>From: {current.created_by_name}</p>
          <p>Posted: {new Date(current.created_at).toLocaleString()}</p>
        </div>
        
        {/* Confirmation Checkbox */}
        <div className={`flex items-start gap-3 p-4 rounded-lg ${config.bgColor} ${config.borderColor} border mt-2`}>
          <Checkbox
            id="confirm-acknowledgment"
            checked={confirmed}
            onCheckedChange={setConfirmed}
            className={`mt-0.5 ${config.checkboxColor}`}
            data-testid="confirm-acknowledgment-checkbox"
          />
          <Label 
            htmlFor="confirm-acknowledgment" 
            className={`text-sm cursor-pointer leading-relaxed ${config.textColor}`}
          >
            I have read and understood this announcement, and I commit to comply with its contents.
          </Label>
        </div>
        
        <DialogFooter className="flex items-center justify-between sm:justify-between gap-2 mt-2">
          {announcements.length > 1 ? (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevious}
                disabled={currentIndex === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNext}
                disabled={currentIndex === announcements.length - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div />
          )}
          <Button 
            onClick={handleAcknowledge} 
            disabled={acknowledging || !confirmed}
            className={`min-w-[160px] ${!confirmed ? 'opacity-50' : ''}`}
            data-testid="acknowledge-announcement-btn"
          >
            {acknowledging ? (
              'Acknowledging...'
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                I Acknowledge
              </>
            )}
          </Button>
        </DialogFooter>
        
        {!confirmed && (
          <p className="text-xs text-center text-muted-foreground">
            Please check the box above to enable acknowledgment
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForcedAnnouncementPopup;
